import { useEffect, useMemo, useState, type CSSProperties, type ReactNode } from 'react';

import { geo as geoUtil, type AgentOperatorBackref, type OutletDirectoryRow } from '@kilat/shared';
import { GeoMap, type GeoMarker, type GeoMarkerTone } from '@kilat/ui';

import { useAgentId, useAuth } from '../lib/use-auth.js';
import { useI18n } from '../lib/use-i18n.js';
import { operatorsBackrefRepoFor } from '../lib/repositories/earnings-repo.js';
import { listListedOutlets } from '../lib/repositories/outlet-directory-repo.js';
import { logger } from '../lib/logger.js';

/**
 * /map — the operators an agent has signed, plotted at their outlet
 * coordinates (feature: map-dashboards).
 *
 * Coordinate source: the agent's `agents/{agentId}/operators/{tenantId}`
 * back-refs carry no geo (agents can't read `tenants/**`), so this joins them
 * to the PUBLIC find directory (`outletDirectory`, listed rows only — the sole
 * read a non-super-admin is allowed) by `tenantId`. Reuses the shared
 * <GeoMap>. Operators whose outlets aren't listed / located fall into a "not
 * on the map" list (no silent drop). Pure reads; no money / auth / write paths.
 */

type BackrefStatus = NonNullable<AgentOperatorBackref['currentStatus']>;

type LoadState =
  | { readonly phase: 'loading' }
  | {
      readonly phase: 'ready';
      readonly backrefs: readonly AgentOperatorBackref[];
      readonly rowsByTenant: ReadonlyMap<string, readonly OutletDirectoryRow[]>;
    }
  | { readonly phase: 'error' };

function statusTone(status: BackrefStatus): GeoMarkerTone {
  switch (status) {
    case 'active':
      return 'positive';
    case 'trial':
      return 'primary';
    case 'past_due':
      return 'warning';
    case 'suspended':
      return 'danger';
    case 'archived':
      return 'neutral';
    default:
      return 'neutral';
  }
}

export function MapPage() {
  const { user, loading: authLoading } = useAuth();
  const agentId = useAgentId();
  const t = useI18n();

  const [state, setState] = useState<LoadState>({ phase: 'loading' });
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    if (!agentId) return;
    let cancelled = false;
    setState({ phase: 'loading' });
    Promise.all([operatorsBackrefRepoFor(agentId).list(), listListedOutlets()])
      .then(([backrefs, rows]) => {
        if (cancelled) return;
        const rowsByTenant = new Map<string, OutletDirectoryRow[]>();
        for (const row of rows) {
          if (!geoUtil.hasRealLocation(row.geo)) continue;
          const list = rowsByTenant.get(row.tenantId) ?? [];
          list.push(row);
          rowsByTenant.set(row.tenantId, list);
        }
        setState({ phase: 'ready', backrefs, rowsByTenant });
      })
      .catch((e: unknown) => {
        logger.error('agent map: load failed', { error: String(e) });
        if (!cancelled) setState({ phase: 'error' });
      });
    return () => {
      cancelled = true;
    };
  }, [agentId, attempt]);

  const view = useMemo(() => {
    if (state.phase !== 'ready') {
      return { markers: [] as GeoMarker[], unmapped: [] as AgentOperatorBackref[] };
    }
    const markers: GeoMarker[] = [];
    const unmapped: AgentOperatorBackref[] = [];
    for (const b of state.backrefs) {
      const status: BackrefStatus = b.currentStatus ?? 'trial';
      const name = b.tenantBusinessName ?? b.tenantId ?? t.t('agent.map.operatorFallback');
      const rows = b.tenantId ? state.rowsByTenant.get(b.tenantId) : undefined;
      if (!rows || rows.length === 0) {
        unmapped.push(b);
        continue;
      }
      for (const row of rows) {
        markers.push({
          id: `${b.tenantId ?? b.id}:${row.id}`,
          lat: row.geo.lat,
          lng: row.geo.lng,
          label: name,
          tone: statusTone(status),
          popup: (
            <OperatorPopup
              name={name}
              outlet={row.name}
              address={row.address}
              statusLabel={t.t(`agent.map.status.${status}`)}
            />
          ),
        });
      }
    }
    return { markers, unmapped };
  }, [state, t]);

  return (
    <Shell>
      <header style={{ marginBottom: 'var(--space-2)' }}>
        <h1 style={{ margin: 0, fontSize: 'var(--text-h2)', fontWeight: 600 }}>
          {t.t('agent.map.title')}
        </h1>
        <p style={subtitleStyle}>{t.t('agent.map.subtitle')}</p>
      </header>

      {authLoading || (agentId && state.phase === 'loading') ? (
        <StatusCard text={t.t('agent.map.loading')} />
      ) : !user ? (
        <Card title={t.t('agent.map.signedOut')} />
      ) : !agentId ? (
        <Card title={t.t('agent.map.notAgent')} />
      ) : state.phase === 'error' ? (
        <Card
          title={t.t('agent.map.errorTitle')}
          body={t.t('agent.map.errorBody')}
          action={{ label: t.t('agent.map.retry'), onClick: () => setAttempt((a) => a + 1) }}
        />
      ) : state.phase === 'ready' && state.backrefs.length === 0 ? (
        <Card
          title={t.t('agent.map.empty')}
          action={{ label: t.t('agent.map.openCard'), href: '/code' }}
        />
      ) : view.markers.length === 0 ? (
        <Card
          title={t.t('agent.map.unmappedTitle', { count: view.unmapped.length })}
          body={t.t('agent.map.unmappedBody')}
        />
      ) : (
        <>
          <GeoMap markers={view.markers} ariaLabel={t.t('agent.map.mapAria')} height={420} />
          {view.unmapped.length > 0 ? (
            <section style={cardStyle}>
              <h2
                style={{
                  margin: '0 0 var(--space-1)',
                  fontSize: 'var(--text-body)',
                  fontWeight: 600,
                }}
              >
                {t.t('agent.map.unmappedTitle', { count: view.unmapped.length })}
              </h2>
              <p style={{ margin: '0 0 var(--space-3)', ...subtitleStyle }}>
                {t.t('agent.map.unmappedBody')}
              </p>
              <ul style={{ margin: 0, padding: 0, listStyle: 'none', display: 'grid', gap: 6 }}>
                {view.unmapped.map((b) => (
                  <li
                    key={b.tenantId ?? b.id ?? b.tenantBusinessName}
                    style={{
                      fontSize: 'var(--text-meta)',
                      display: 'flex',
                      justifyContent: 'space-between',
                      gap: 8,
                    }}
                  >
                    <span>
                      {b.tenantBusinessName ?? b.tenantId ?? t.t('agent.map.operatorFallback')}
                    </span>
                    <span style={{ color: 'var(--color-on-surface-muted)' }}>
                      {t.t(`agent.map.status.${b.currentStatus ?? 'trial'}`)}
                    </span>
                  </li>
                ))}
              </ul>
            </section>
          ) : null}
        </>
      )}
    </Shell>
  );
}

function OperatorPopup({
  name,
  outlet,
  address,
  statusLabel,
}: {
  readonly name: string;
  readonly outlet: string;
  readonly address: string;
  readonly statusLabel: string;
}) {
  return (
    <div style={{ display: 'grid', gap: 4, minWidth: 150 }}>
      <strong style={{ fontSize: 'var(--text-body)' }}>{name}</strong>
      {outlet !== name ? (
        <span style={{ fontSize: 'var(--text-meta)', color: 'var(--color-on-surface-muted)' }}>
          {outlet}
        </span>
      ) : null}
      <span style={{ fontSize: 'var(--text-meta)', color: 'var(--color-on-surface-muted)' }}>
        {statusLabel}
      </span>
      <span style={{ fontSize: 'var(--text-meta)', color: 'var(--color-on-surface-muted)' }}>
        {address}
      </span>
    </div>
  );
}

function Shell({ children }: { readonly children: ReactNode }) {
  return (
    <main
      className="ag-page"
      style={{
        minHeight: '100dvh',
        background: 'var(--tint-subtle)',
        padding: 'var(--space-4) var(--space-4) var(--space-12) var(--space-4)',
        display: 'flex',
        flexDirection: 'column',
        gap: 'var(--space-4)',
        margin: '0 auto',
      }}
    >
      {children}
    </main>
  );
}

function StatusCard({ text }: { readonly text: string }) {
  return (
    <div style={cardStyle} role="status" aria-live="polite">
      <p style={{ margin: 0, ...subtitleStyle }}>{text}</p>
    </div>
  );
}

function Card({
  title,
  body,
  action,
}: {
  readonly title: string;
  readonly body?: string;
  readonly action?: {
    readonly label: string;
    readonly href?: string;
    readonly onClick?: () => void;
  };
}) {
  return (
    <section style={{ ...cardStyle, textAlign: 'center' }}>
      <h2 style={{ margin: 0, fontSize: 'var(--text-h2)', fontWeight: 600 }}>{title}</h2>
      {body ? (
        <p
          style={{
            margin: 'var(--space-3) auto 0',
            maxWidth: 420,
            ...subtitleStyle,
            lineHeight: 1.5,
          }}
        >
          {body}
        </p>
      ) : null}
      {action ? (
        action.href ? (
          <a href={action.href} style={{ ...ctaStyle, marginTop: 'var(--space-4)' }}>
            {action.label}
          </a>
        ) : (
          <button
            type="button"
            onClick={action.onClick}
            style={{ ...ctaStyle, marginTop: 'var(--space-4)', border: 'none', cursor: 'pointer' }}
          >
            {action.label}
          </button>
        )
      ) : null}
    </section>
  );
}

const cardStyle: CSSProperties = {
  background: 'var(--color-surface)',
  borderRadius: 'var(--radius-lg)',
  padding: 'var(--space-5)',
  boxShadow: 'var(--shadow-1)',
};

const subtitleStyle: CSSProperties = {
  margin: 'var(--space-1) 0 0 0',
  fontSize: 'var(--text-meta)',
  color: 'var(--color-on-surface-muted)',
};

const ctaStyle: CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  padding: 'var(--space-2) var(--space-4)',
  minHeight: 'var(--touch-target, 44px)',
  borderRadius: 'var(--radius-pill)',
  background: 'var(--color-primary)',
  color: 'var(--color-primary-fg)',
  textDecoration: 'none',
  fontSize: 'var(--text-meta)',
  fontWeight: 600,
};
